import { CoreInputModelUtil, CoreModel, getNewErrors, InputErrorModel, UtilityMethods } from "@mindmemobile/mm-shared-global";
import { EditorState } from "draft-js"; 
import { autorun } from "mobx"; 
import { MediaModel } from "./MediaModel";
import { CheckOutRedirectTypes } from "../enums"; 
import { IAPICustomOptionModel } from "../interfaces/IAPICheckoutModel";
 

export class CustomOptionModel extends CoreModel {
    id: string;
    media?: MediaModel;
    name: string;
    description: string;
    descriptionEditorState: EditorState;
    titleEditorRawContent: string;
    descriptionEditorRawContent: string;
    showPrice: boolean;
    priceText: string;
    showButton: boolean;
    redirectType: keyof typeof CheckOutRedirectTypes;
    redirectValue: string;
    buttonText: string;
    errors: InputErrorModel<CustomOptionModel>[];

    constructor(data?: Partial<CustomOptionModel>) {
        super();
        this.id = data?.id || '';
        this.media = data && data.media && new MediaModel(data.media);
        this.name = data?.name || '';
        this.description = data?.description || '';
        this.descriptionEditorState = data?.descriptionEditorState || EditorState.createEmpty();
        this.titleEditorRawContent = data?.titleEditorRawContent || '';
        this.descriptionEditorRawContent = data?.descriptionEditorRawContent || '';
        this.showPrice = data?.showPrice || false;
        this.priceText = data?.priceText || '';
        this.errors = data?.errors || [];
        this.redirectType =
            data?.redirectType ||
            (Object.keys(CheckOutRedirectTypes).find(
                (key) =>
                    CheckOutRedirectTypes[key as keyof typeof CheckOutRedirectTypes] === CheckOutRedirectTypes.Website,
            ) as keyof typeof CheckOutRedirectTypes);
        this.redirectValue = data?.redirectValue || '';
        this.buttonText = data?.buttonText || '';
        this.showButton = data?.showButton || false;
        autorun(() => {
            this.validate(true);
        });
    }

    hasError(key: keyof CustomOptionModel) {
        return this.errors.find((f) => f.field == key) !== undefined;
    }

    validateRedirectTypes(newErrors: InputErrorModel<CustomOptionModel>[]) {
        switch (CheckOutRedirectTypes[this.redirectType]) {
            case CheckOutRedirectTypes.Website:
                CoreInputModelUtil.validateWebsite('redirectValue', this.redirectValue, newErrors);
                break;
            case CheckOutRedirectTypes.ClickToEmail:
                CoreInputModelUtil.validateEmail('redirectValue', this.redirectValue, newErrors);
                break;
            case CheckOutRedirectTypes.ClickToCall:
                CoreInputModelUtil.validatePhoneNumber('redirectValue', this.redirectValue, newErrors);
                break;
            case CheckOutRedirectTypes.ClickToText:
                CoreInputModelUtil.validateTextField(this.redirectValue, 'redirectValue', newErrors);
                break;
        }
    }

    validate(isAutoRun: boolean = false) {
        const newErrors: InputErrorModel<CustomOptionModel>[] = [];

        CoreInputModelUtil.validateTextField(this.name, 'name', newErrors);
        if (this.showPrice) {
            CoreInputModelUtil.validateTextField(this.priceText, 'priceText', newErrors);
        }
        if (this.showButton) {
            this.validateRedirectTypes(newErrors);
            CoreInputModelUtil.validateTextField(this.buttonText, 'buttonText', newErrors);
        }

        if (isAutoRun) {
            this.errors = getNewErrors(newErrors, this.errors);
        } else {
            this.errors = newErrors;
        }
        return this.errors.length === 0;
    }

    createAPIModel(): IAPICustomOptionModel {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            descriptionEditorRawContent: this.descriptionEditorRawContent,
            showPrice: this.showPrice,
            priceButtonText: this.priceText,
            showButton: this.showButton,
            redirect: {
                redirectType: this.redirectType,
                redirectValue: this.redirectValue,
            },
            buttonText: this.buttonText,
            media: this.media && this.media.createAPIModel(),
        };
    }

    static deserializeAPIModel = (data: IAPICustomOptionModel): CustomOptionModel => {
        if (!data) return new CustomOptionModel();
        return new CustomOptionModel({
            id: data.id,
            name: data.name,
            description: data.description,
            descriptionEditorState: UtilityMethods.parseEditorState(data.descriptionEditorRawContent),
            descriptionEditorRawContent: data.descriptionEditorRawContent,
            showPrice: data.showPrice,
            priceText: data.priceButtonText,
            showButton: data.showButton,
            redirectType: data.redirect.redirectType,
            redirectValue: data.redirect.redirectValue,
            buttonText: data.buttonText,
            media: data.media && MediaModel.deserializeAPIModel(data.media),
        });
    };
}
